import { is_unfi_portal_thread } from "@/deduction_detail/RightPane/Conversation"
import { generate_message, type GenerateMessageType } from "@/api"
import { format, parseISO } from "date-fns"
import { RightPaneProps } from "@/deduction_detail/RightPane"
import { Loader2 } from "lucide-react"
import { useState } from "preact/hooks"
import { unreachable } from "@/utils"
import { requestBackup, useBackupRequestInFlight } from "."
import { AssignmentForm } from "@/deduction_table/assignment_form"
import { playbook_action_hint } from "@/deduction/dispute_playbook.ts"
import { toast } from "@/components/ui/use-toast"

export function NextActionCard({ deduction, className, onTriggerWizard, onFileDispute, onMatchOriginal }: RightPaneProps) {
  const [loadingBumpMessage, setLoadingBumpMessage] = useState(false)
  const backupRequestInFlight = useBackupRequestInFlight(deduction.id)
  const backupRequestDisabled = backupRequestInFlight || deduction.has_backup

  /** Write a message into the thread's reply box. `kind` picks which one the server composes. */
  async function handlePopulateMessage(kind: GenerateMessageType, failure_title: string) {
    setLoadingBumpMessage(true)
    const res = await generate_message(deduction.id, kind)
    setLoadingBumpMessage(false)

    if (!res.ok) {
      toast({ title: failure_title, description: res.error.message, variant: "destructive" })
      return
    }
    // Find the reply textarea and populate it
    const textarea = document.querySelector('[data-ai-message-target="true"]') as HTMLTextAreaElement | null
    if (!textarea) {
      toast({
        title: "No reply box found",
        description: "There is no conversation thread on this deduction to reply to.",
        variant: "destructive",
      })
      return
    }
    textarea.value = res.value.data.message
    textarea.dispatchEvent(new Event('input', { bubbles: true }))
    textarea.focus()
    textarea.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  let action = deduction.action
  if (!action) {
    return null
  }
  let action_title
  let action_el
  if (deduction.action) {
    let action = deduction.action
    if (action.type === "request_backup") {
      action_title = action.is_rerequest ? "Re-request Backup" : "Request Backup"
      let message = action.is_rerequest
        ? "We already requested backup but haven't heard back. Consider sending another request."
        : "This deduction has no associated backup."
      // Where the request goes, when the retailer publishes it (see "How to dispute" on the page).
      let hint = playbook_action_hint(deduction.dispute_playbook, "request_backup")
      action_el = (
        <div class="flex flex-col">
          <div>{message}</div>
          {hint && <div class="text-xs text-slate-500 mt-1">{hint}</div>}
          <div class="flex justify-end">
            <button
              class="py-2 px-4 bg-emerald-500 hover:bg-emerald-600 text-white rounded-md shadow transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-opacity-50 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => !backupRequestDisabled && requestBackup(deduction, { is_rerequest: action.is_rerequest })}
              disabled={backupRequestDisabled}
              title={deduction.has_backup ? "Backup already synced from portal" : undefined}>
              {backupRequestInFlight
                ? "Requesting…"
                : action.is_rerequest
                  ? "Re-request Backup"
                  : "Request Backup"}
            </button>
          </div>
        </div>
      )
    } else if (action.type === "file_dispute") {
      action_title = "File Dispute"
      let description
      if (action.dispute_type === "earlypay") {
        description =
          "This invoice has an early pay discount applied, but it was outside the contractual early payment window."
      } else {
        description = "This deduction is disputable"
      }
      let hint = playbook_action_hint(deduction.dispute_playbook, "file_dispute", deduction.category_dispute_email_to)
      action_el = (
        <div class="flex flex-col">
          <div>{description}</div>
          {hint && <div class="text-xs text-slate-500 mt-1">{hint}</div>}
          <div class="flex justify-end">
            <button
              class="py-2 px-4 bg-emerald-500 hover:bg-emerald-600 text-white rounded-md shadow transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-opacity-50 cursor-pointer"
              onClick={() => onFileDispute?.()}>
              File Dispute
            </button>
          </div>
        </div>
      )
    } else if (action.type === "send_followup") {
      action_title = "Send Followup"
      const unfi_portal_thread = is_unfi_portal_thread(deduction.messages)
      let message
      if (action.backup_requested_on) {
        // They answered the backup request without covering this charge — the common shape is a
        // ten-invoice request coming back with nine documents.
        message = `They replied to the backup request sent ${format(parseISO(action.backup_requested_on), "MMM d, yyyy")}, but this charge still has no backup. Follow up on the same thread.`
      } else if (unfi_portal_thread) {
        message = "UNFI answered in its Dispute Center, which doesn't take replies. Use File Dispute to follow up: a denied claim goes in as its appeal."
      } else if (action.havent_heard) {
        message = "We haven't heard back yet from the dispute. Reach out to them again to bump the dispute."
      } else {
        message = "We got a reply from the dispute, and we need to follow up."
      }
      action_el = (
        <div class="flex flex-col">
          <div>{message}</div>
          {unfi_portal_thread && !action.backup_requested_on && (
            <div class="flex justify-end mt-2">
              <button
                class="py-2 px-4 bg-emerald-500 hover:bg-emerald-600 text-white rounded-md shadow transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-opacity-50 cursor-pointer"
                onClick={() => onFileDispute?.()}>
                File Dispute
              </button>
            </div>
          )}
          {action.backup_requested_on && (
            <div class="flex justify-end mt-2">
              <button
                class="py-2 px-4 bg-emerald-500 hover:bg-emerald-600 text-white rounded-md shadow transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-opacity-50 cursor-pointer flex items-center disabled:opacity-50"
                onClick={() => handlePopulateMessage("backup_followup", "Could not write follow-up")}
                disabled={loadingBumpMessage}>
                {loadingBumpMessage ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  "Send Followup"
                )}
              </button>
            </div>
          )}
        </div>
      )
    } else if (action.type === "waiting_for_response") {
      action_title = "Waiting for Response"
      action_el = (
        <div class="flex flex-col">
          <div>We've sent a message. Hold tight until we hear back.</div>
        </div>
      )
    } else if (action.type === "waiting_for_document") {
      action_title = "Waiting for Document"
      action_el = (
        <div class="flex flex-col">
          <div>A team member is working on pulling the document.</div>
        </div>
      )
    } else if (action.type === "assign_task") {
      action_title = "Assign Task"
      action_el = (
        <div class="flex flex-col">
          <div>Assign task to team member</div>
        </div>
      )
    } else if (action.type === "review_backup") {
      action_title = "Review Backup"
      action_el = (
        <div class="flex flex-col">
          <div>Review backup</div>
        </div>
      )
    } else if (action.type === "bump_message") {
      action_title = "Bump Message"
      action_el = (
        <div class="flex flex-col">
          <div>We sent a message {action.days_since_last_message} day{action.days_since_last_message === 1 ? "" : "s"} ago. Send a follow-up to bump the thread.</div>
          <div class="flex justify-end mt-2">
            <button
              class="py-2 px-4 bg-emerald-500 hover:bg-emerald-600 text-white rounded-md shadow transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-opacity-50 cursor-pointer flex items-center disabled:opacity-50"
              onClick={() => handlePopulateMessage("bump_message", "Could not write bump message")}
              disabled={loadingBumpMessage}>
              {loadingBumpMessage ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                "Write Bump Message"
              )}
            </button>
          </div>
        </div>
      )
    } else if (action.type === "match_original") {
      action_title = "Match Original"
      action_el = (
        <div class="flex flex-col">
          <div>This repayment needs to be matched to the original deduction.</div>
          <div class="flex justify-end mt-2">
            <button
              class="py-2 px-4 bg-emerald-500 hover:bg-emerald-600 text-white rounded-md shadow transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-opacity-50 cursor-pointer"
              onClick={() => onMatchOriginal?.()}>
              Match Original
            </button>
          </div>
        </div>
      )
    } else if (action.type === "ask_the_wizard") {
      action_title = "Ask the Wizard"
      action_el = (
        <div class="flex flex-col">
          <div>Run the AI wizard to analyze this deduction and determine the best course of action.</div>
          <div class="flex justify-end mt-2">
            <button
              class="py-2 px-4 bg-emerald-500 hover:bg-emerald-600 text-white rounded-md shadow transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-opacity-50 cursor-pointer"
              onClick={() => onTriggerWizard?.()}>
              Ask the Wizard
            </button>
          </div>
        </div>
      )
    } else if (action.type === "validate_deduction") {
      action_title = "Validate Deduction"
      action_el = (
        <div class="flex flex-col">
          <div>The wizard found this deduction is not disputable. Review and validate.</div>
        </div>
      )
    } else {
      unreachable(action)
      return null
    }
  }
  return (
    <div id="next-action" class="border-t-6 border-emerald-500 bg-emerald-50 rounded-md p-4 space-y-2">
      <span class="flex">
        <div class="w-1 bg-emerald-500 mr-2" />
        <h3 class="text-lg font-semibold leading-5">{action_title}</h3>
      </span>
      {action_el}
    </div>
  )
}
